import { FC, useMemo, useState } from 'react';
import { Text, Box, FormControl, InputGroup, InputLeftElement, Input, Stack, FormErrorMessage, Link } from '@chakra-ui/react';
import { v4 as uuid } from 'uuid';
import { Field, FieldProps, Form, Formik } from 'formik';
import { CreateAccountFields as Fields } from './types';
import { createAccountSchema as formSchema } from './createAccountSchema';
import { createAccountSchemaValidation as validationSchema } from './createAccountValidation';
import { ButtonSubmit, EnterAnimation, FormAlert } from "components";
import { getIcon } from "lib/icons";
import { createDefaultPack } from 'lib/calls';
import { teamAppUrl } from 'lib/utils'
import Axios, { AxiosError } from 'axios';
import { loginClient, teamInvitesClient, teamUsersClient, userClient, usersClient } from "lib/clients";
import { useRouter } from "next/router";
import { User } from "lib/clients/client-users/types";
import { Identities } from 'domains/login';
import { config } from "lib";
import { generateKeys, sha512 } from 'lib/e2ee';
import { useLastVisitedShare } from 'lib/hooks';

const initialValues: Record<keyof typeof Fields, string> = {
  [Fields.first_name]: '',
  [Fields.last_name]: '',
  [Fields.email]: '',
  [Fields.password]: '',
}

type State = {
  error: string;
  success: string;
  redirectTo?: string;
}

type ErrorData = {
  field: string;
  message: string;
  type: string;
}

type Props = {
  defaultEmail?: string;
}

const DEFAULT_ERROR = 'There was an error, please try again';

const formatEmailError = (data: ErrorData): string => {
  switch (data.type) {
    case 'email_exists':
      return 'The email address you entered already exists in our database';
    default:
      return DEFAULT_ERROR;
  }
}

const prettifyError = (error: AxiosError): string => {
  switch (error.response.data.field) {
    case 'email':
      return formatEmailError(error.response.data);
    default:
      return DEFAULT_ERROR;
  }
}

const initialState = { error: '', success: '', redirectTo: null };

export const CreateAccountForm: FC<Props> = ({ defaultEmail }) => {
  const [ state, setState ] = useState<State>(initialState);
  const { push, query: { next, referrer } } = useRouter();
  const { join } = useLastVisitedShare();

  const values = useMemo(() => ({ ...initialValues, email: defaultEmail || '' }), [defaultEmail]);

  const onSubmit = async (values: Omit<User, 'has_avatar'>): Promise<void> => {
    try {
      setState({ ...initialState });

      const user = {...values} as any;

      const password = user.password;

      user.password = sha512(user.password); // Hash the password so that the value that is sent to the server is not the same as the passphrase

      const userId = { email: user.email, name: `${user.first_name} ${user.last_name}`};

      const { privateKeyArmored, publicKeyArmored } = await generateKeys(userId, password);

      user.private_key_armored = privateKeyArmored;
      user.public_key_armored = publicKeyArmored;

      // Check the users invites
      const { data: invites } = await teamInvitesClient.getInvitesByEmail(user.email);

      // If they have invites, we just create a user account with no team
      if (invites.length) {
        await usersClient.createUser({ ...user });
        await loginClient.postLogin(user.email, user.password);

        let route = '/teams';

        if (referrer === config.ad.singlePack.param) {
          route += `?referrer=${referrer}`;
        }

        await push(route);
        return;
      }

      // If they don't, we create a user with a team and also a default pack
      const { data: { team } } = await teamUsersClient.createUser({ user: { ...user } })
      await loginClient.postLogin(user.email, user.password);

      const { data: pack } = await createDefaultPack(team.identifier)
      await join(team.identifier, user.public_key_armored);

      const teamUrl = teamAppUrl(team.identifier);

      let redirect = `${teamUrl}/packs/${pack.id}`;

      if (referrer === config.ad.singlePack.param) {
        redirect = `${teamUrl}/packs`;
      }

      return setState({ ...state, redirectTo: redirect });
    } catch (err) {
      if (Axios.isAxiosError(err)) {
        const error = prettifyError(err);
        setState({ ...state, error });
        return;
      }

      setState({ ...state, error: DEFAULT_ERROR });
    }
  }

  const redirect = async (user: any): Promise<void> => {
    const { data: teams } = await userClient.getTeams();
    const { data: invites } = await teamInvitesClient.getInvitesByEmail(user.email);

    // If they have multiple teams, let them pick
    // If they have any invites, show them
    // If they have no invites AND no teams, send them to the teams page so they can create one.
    if (
      teams.length > 1 ||
      invites.length > 0 ||
      (invites.length === 0 && teams.length === 0)
    ) {
      let route = `/teams?next=${next || '/packs'}`;

      if (referrer) {
        route += `&referrer=${referrer}`
      }

      await push(route); // No need to display animation, that will happen after the teams page.
      return;
    }

    // If the user has just the one team take them straight there
    const teamUrl = teamAppUrl(teams[0].identifier);
    setState({ ...state, redirectTo: teamUrl + (next || '/packs')}); // Going to the packs page, so display animation.
    return;
  }

  if (state.redirectTo) {
    return <EnterAnimation onComplete={() => window.location.replace(state.redirectTo)} />;
  }

  return (
    <>
      <Identities onSuccess={(user: any) => redirect(user)} />
      <Formik
        initialValues={values}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <Form>
          <Stack spacing={6} width="100%">
            <FormAlert success={state.success} error={state.error} />
            {formSchema.map((schema) => (
              <Field key={uuid()} name={schema.name}>
                {({field, form, meta}: FieldProps) => (
                  <FormControl
                    isInvalid={Boolean(meta.touched && form.errors[field.name])}
                    isRequired={true}
                  >
                    <InputGroup size="md">
                      <InputLeftElement height="100%" width="30px" ml="15px">
                        {getIcon(schema.icon)}
                      </InputLeftElement>
                      <Input pr="2rem" pl="4rem" size="lg"{...schema} {...field} />
                    </InputGroup>
                    <FormErrorMessage>{form.errors[field.name]}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
            ))}
            <Box width="100%" textAlign="center">
              <Text size="sm">
                By clicking the button below, you indicate that you agree to the <Link href='/privacy_policy.pdf' isExternal>privacy policy</Link> and <Link href="/terms_and_conditions.pdf" isExternal>terms of service</Link>. This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and <a href="https://policies.google.com/terms" rel="noreferrer" target="_blank">Terms of Service</a> apply.
              </Text>
            </Box>
            <ButtonSubmit isFullWidth={true} size="xl">Create Account</ButtonSubmit>
          </Stack>
        </Form>
      </Formik>
    </>
  )
}
